import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { readingApi } from '../../services/readingApi';
import '../../styles/ArticleFeedback.css';

const ArticleFeedback = () => {
  const { articleId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Extract email parameter (articleId comes from URL params)
  const email = searchParams.get('email');
  
  // State variables
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [articleData, setArticleData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    rating: '',
    confidenceBefore: '',
    confidenceAfter: '',
    problemsSolved: [],
    liked: '',
    improvementSuggestions: '',
    additionalFeedback: ''
  });
  
  // Form validation
  const [errors, setErrors] = useState({});
  
  // Fetch article data on component mount
  useEffect(() => {
    const fetchArticleData = async () => {
      try {
        if (!email || !articleId) {
          throw new Error('Missing required parameters: email and articleId');
        }
        
        setLoading(true);
        const data = await readingApi.getArticleDetails(email, articleId);
        setArticleData(data);
        setLoading(false);
      } catch (err) {
        setError(err.message || 'Failed to load article data');
        setLoading(false);
      }
    };
    
    fetchArticleData();
  }, [email, articleId]);
  
  // Get descriptive label for confidence levels
  const getConfidenceLabel = (value) => {
    switch(value) {
      case 1: return 'Not confident at all';
      case 2: return 'Slightly confident';
      case 3: return 'Moderately confident';
      case 4: return 'Very confident';
      case 5: return 'Extremely confident';
      default: return '';
    }
  };

  // Get descriptive label for rating scale
  const getRatingLabel = (value) => {
    switch(value) {
      case 1: return 'Not helpful';
      case 2: return 'Somewhat helpful';
      case 3: return 'Helpful';
      case 4: return 'Very helpful';
      case 5: return 'Extremely helpful';
      default: return '';
    }
  };

  // Handle checkbox changes for problems solved
  const handleCheckboxChange = (problemIndex) => {
    const updatedProblems = [...formData.problemsSolved];
    
    // Add or remove the problem index
    if (updatedProblems.includes(problemIndex)) {
      // Remove if already selected
      const index = updatedProblems.indexOf(problemIndex);
      updatedProblems.splice(index, 1);
    } else {
      // Add if not selected
      updatedProblems.push(problemIndex);
    }
    
    setFormData({ ...formData, problemsSolved: updatedProblems });
    
    // Clear validation error if at least one problem is selected
    if (updatedProblems.length > 0 && errors.problemsSolved) {
      const { problemsSolved, ...restErrors } = errors;
      setErrors(restErrors);
    }
  };
  
  // Handle radio button changes
  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: parseInt(value, 10) });
    
    // Clear validation error for this field
    if (errors[name]) {
      const { [name]: _, ...restErrors } = errors;
      setErrors(restErrors);
    }
  };
  
  // Handle text input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };
  
  // Validate form before submission
  const validateForm = () => {
    const newErrors = {};
    
    if (formData.problemsSolved.length === 0) {
      newErrors.problemsSolved = 'Please select at least one problem you solved';
    }
    
    if (!formData.confidenceBefore) {
      newErrors.confidenceBefore = 'Please rate your confidence before reading';
    }
    
    if (!formData.confidenceAfter) {
      newErrors.confidenceAfter = 'Please rate your confidence after reading';
    }
    
    if (!formData.rating) {
      newErrors.rating = 'Please rate this article';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Prepare data for submission
      const feedbackData = {
        email: email,
        article_id: parseInt(articleId, 10),
        group_id: articleData.group_id,
        rating: formData.rating,
        confidence_before: formData.confidenceBefore,
        confidence_after: formData.confidenceAfter,
        problems_solved: formData.problemsSolved,
        liked: formData.liked || null,
        improvement_suggestions: formData.improvementSuggestions || null,
        additional_feedback: formData.additionalFeedback || null
      };
      
      // Submit feedback
      await readingApi.submitFeedback(feedbackData);
      
      // Show success message and reset form
      setSubmitSuccess(true);
      setSubmitting(false);
      
      // Scroll to top to show success message
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.message || 'Failed to submit feedback');
      setSubmitting(false);
    }
  };
  
  // Return to homepage
  const handleReturnHome = () => {
    navigate('/');
  };
  
  if (loading) {
    return (
      <div className="article-feedback-container">
        <div className="feedback-card">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading article information...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="article-feedback-container">
        <div className="feedback-card error-card">
          <h2>Error</h2>
          <p>{error}</p>
          <button className="primary-button" onClick={handleReturnHome}>Return to Homepage</button>
        </div>
      </div>
    );
  }
  
  if (submitSuccess) {
    return (
      <div className="article-feedback-container">
        <div className="success-container">
          <div className="success-icon">🎉</div>
          <h2 className="success-title">PrepLetter #{articleId} Complete!</h2>
          <p className="success-message">
            You've successfully completed PrepLetter #{articleId}! Not everyone finishes their daily PrepLetter, 
            and you're one of the dedicated few who stayed committed. That's no small feat!
            <br /><br />
            Keep up the incredible work - your next PrepLetter will be with you soon! 🚀
          </p>
          <button 
            className="btn btn-primary"
            onClick={handleReturnHome}
            style={{ marginTop: '2rem' }}
          >
            Back to Homepage
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="article-feedback-container">
      <div className="feedback-card">
        <h1>Mark as Read Form</h1>

        {articleData?.already_read && (
          <div className="message-box success">
            <p>
              You've already marked this PrepLetter as read. ✅ Thank you for
              your commitment to learning! Your feedback is still valuable to
              us.
            </p>
          </div>
        )}

        {!articleData?.already_read && (
          <div className="message-box">
            <p>
              Great job completing this article! Let's mark the PrepLetter as
              read so you can get the next one. Your feedback will help us
              improve PrepLetter and tailor the content to better suit your
              learning journey.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>
              Which problems did you solve? Or believe you can solve now?{" "}
              <span className="required">*</span>
            </label>
            <div className="checkbox-group">
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="problem0"
                  checked={formData.problemsSolved.includes(0)}
                  onChange={() => handleCheckboxChange(0)}
                />
                <span className="checkmark"></span>
                <label htmlFor="problem0">
                  {articleData?.problems?.main_problem}
                </label>
              </div>
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="problem1"
                  checked={formData.problemsSolved.includes(1)}
                  onChange={() => handleCheckboxChange(1)}
                />
                <span className="checkmark"></span>
                <label htmlFor="problem1">
                  {articleData?.problems?.similar_problem_1}
                </label>
              </div>
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="problem2"
                  checked={formData.problemsSolved.includes(2)}
                  onChange={() => handleCheckboxChange(2)}
                />
                <span className="checkmark"></span>
                <label htmlFor="problem2">
                  {articleData?.problems?.similar_problem_2}
                </label>
              </div>
              {errors.problemsSolved && (
                <p className="error-message">{errors.problemsSolved}</p>
              )}
              <p className="hint">
                Please be honest - this helps us understand which problems are
                most engaging.
              </p>
            </div>
          </div>

          <div className="form-group">
            <label>
              BEFORE reading this PrepLetter How confident were you about
              solving the problems? <span className="required">*</span>
            </label>
            <div className="radio-group">
              {[1, 2, 3, 4, 5].map((value) => (
                <div className="radio-item" key={`before-${value}`}>
                  <input
                    type="radio"
                    id={`before-${value}`}
                    name="confidenceBefore"
                    value={value}
                    checked={formData.confidenceBefore === value}
                    onChange={handleRadioChange}
                  />
                  <span className="radio-mark"></span>
                  <label htmlFor={`before-${value}`}>
                    {value} - {getConfidenceLabel(value)}
                  </label>
                </div>
              ))}
            </div>
            {errors.confidenceBefore && (
              <p className="error-message">{errors.confidenceBefore}</p>
            )}
            <div className="hint">
              1 = Not confident at all, 5 = Very confident
            </div>
          </div>

          <div className="form-group">
            <label>
              AFTER reading this PrepLetter How confident are you about solving
              the problems? <span className="required">*</span>
            </label>
            <div className="radio-group">
              {[1, 2, 3, 4, 5].map((value) => (
                <div className="radio-item" key={`after-${value}`}>
                  <input
                    type="radio"
                    id={`after-${value}`}
                    name="confidenceAfter"
                    value={value}
                    checked={formData.confidenceAfter === value}
                    onChange={handleRadioChange}
                  />
                  <span className="radio-mark"></span>
                  <label htmlFor={`after-${value}`}>
                    {value} - {getConfidenceLabel(value)}
                  </label>
                </div>
              ))}
            </div>
            {errors.confidenceAfter && (
              <p className="error-message">{errors.confidenceAfter}</p>
            )}
            <div className="hint">
              1 = Not confident at all, 5 = Very confident
            </div>
          </div>

          <div className="form-group">
            <label>
              How would you rate this article?{" "}
              <span className="required">*</span>
            </label>
            <div className="radio-group">
              {[1, 2, 3, 4, 5].map((value) => (
                <div className="radio-item" key={`rating-${value}`}>
                  <input
                    type="radio"
                    id={`rating-${value}`}
                    name="rating"
                    value={value}
                    checked={formData.rating === value}
                    onChange={handleRadioChange}
                  />
                  <span className="radio-mark"></span>
                  <label htmlFor={`rating-${value}`}>
                    {value} - {getRatingLabel(value)}
                  </label>
                </div>
              ))}
            </div>
            {errors.rating && <p className="error-message">{errors.rating}</p>}
            <div className="hint">1 = Not helpful, 10 = Extremely helpful</div>
          </div>

          <div className="form-group">
            <label>What did you like about this article?</label>
            <div className="form-control-container">
              <textarea
                name="liked"
                value={formData.liked}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Optional feedback about what you enjoyed..."
                rows="3"
              ></textarea>
            </div>
          </div>

          <div className="form-group">
            <label>How could this PrepLetter be improved?</label>
            <div className="form-control-container">
              <textarea
                name="improvementSuggestions"
                value={formData.improvementSuggestions}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Optional suggestions for improvement..."
                rows="3"
              ></textarea>
            </div>
          </div>

          <div className="form-group">
            <label>Any additional feedback or suggestions?</label>
            <div className="form-control-container">
              <textarea
                name="additionalFeedback"
                value={formData.additionalFeedback}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Any other thoughts..."
                rows="3"
              ></textarea>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="loading-spinner"></span>
                  <span>Submitting...</span>
                </>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ArticleFeedback;
